import React, { useState, useEffect } from 'react';
import { TestTube2, Link2, CheckCircle2, AlertCircle, Loader2, Eye, EyeOff, ChevronDown, ChevronUp } from 'lucide-react';
import { getSlackSettings, updateSlackSettings, testSlackWebhook } from '../api';

const SlackSettings = () => {
    const [settings, setSettings] = useState({
        enabled: false,
        webhook_url: '',
        notify_on_drift_detected: true,
        notify_on_drift_resolved: true,
        include_diff_summary: true,
        include_link: true,
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [showWebhook, setShowWebhook] = useState(false);
    const [hasExistingWebhook, setHasExistingWebhook] = useState(false);
    const [setupOpen, setSetupOpen] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getSlackSettings();
            setSettings({
                enabled: data.enabled,
                webhook_url: '',
                notify_on_drift_detected: data.notify_on_drift_detected,
                notify_on_drift_resolved: data.notify_on_drift_resolved,
                include_diff_summary: data.include_diff_summary,
                include_link: data.include_link,
            });
            setHasExistingWebhook(!!data.webhook_url_masked);
        } catch (err) {
            setError('Failed to load Slack settings');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);
            const updateData = {
                enabled: settings.enabled,
                notify_on_drift_detected: settings.notify_on_drift_detected,
                notify_on_drift_resolved: settings.notify_on_drift_resolved,
                include_diff_summary: settings.include_diff_summary,
                include_link: settings.include_link,
            };
            if (settings.webhook_url && settings.webhook_url.trim()) {
                updateData.webhook_url = settings.webhook_url.trim();
            }
            await updateSlackSettings(updateData);
            setSuccess('Settings saved successfully');
            setSettings(prev => ({ ...prev, webhook_url: '' }));
            setHasExistingWebhook(true);
        } catch (err) {
            const detail = err.response?.data?.detail;
            setError(Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : (detail || 'Failed to save settings'));
        } finally {
            setSaving(false);
        }
    };

    const handleTest = async () => {
        try {
            setTesting(true);
            setError(null);
            setSuccess(null);
            const result = await testSlackWebhook();
            if (result.success) {
                setSuccess(result.message);
            } else {
                setError(result.message);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to test webhook');
        } finally {
            setTesting(false);
        }
    };

    const handleToggle = async (key) => {
        const newValue = !settings[key];

        if (key === 'enabled' && newValue) {
            const hasWebhook = hasExistingWebhook || settings.webhook_url.trim();
            if (!hasWebhook) {
                setError('Please save a webhook URL first before enabling notifications.');
                return;
            }
        }

        const updated = { ...settings, [key]: newValue };
        setSettings(updated);
        setError(null);

        try {
            const updateData = {
                enabled: updated.enabled,
                notify_on_drift_detected: updated.notify_on_drift_detected,
                notify_on_drift_resolved: updated.notify_on_drift_resolved,
                include_diff_summary: updated.include_diff_summary,
                include_link: updated.include_link,
            };
            if (key === 'enabled' && newValue && settings.webhook_url.trim()) {
                updateData.webhook_url = settings.webhook_url.trim();
                setHasExistingWebhook(true);
                setSettings(prev => ({ ...prev, webhook_url: '' }));
            }
            await updateSlackSettings(updateData);
        } catch (err) {
            setSettings(settings);
            const detail = err.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail.map(e => e.msg).join(', ') : (detail || 'Failed to save settings');
            setError(msg);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* Status Messages */}
            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-900/20 border border-red-800/50 rounded-lg">
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                    <span className="text-sm text-red-300">{error}</span>
                </div>
            )}
            {success && (
                <div className="flex items-center gap-2 p-3 bg-green-900/20 border border-green-800/50 rounded-lg">
                    <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0" />
                    <span className="text-sm text-green-300">{success}</span>
                </div>
            )}

            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between p-3 bg-[#252526] rounded-lg">
                <div>
                    <span className="font-medium text-white block text-sm">Enable Slack Notifications</span>
                    <span className="text-xs text-[#808080]">Send alerts when drift is detected or resolved</span>
                </div>
                <button
                    onClick={() => handleToggle('enabled')}
                    className={`relative w-11 h-5 rounded-full transition-colors shrink-0 ${settings.enabled ? 'bg-blue-600' : 'bg-[#4c4c4c]'}`}
                >
                    <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${settings.enabled ? 'left-6' : 'left-0.5'}`} />
                </button>
            </div>

            {/* Webhook URL */}
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium text-[#cccccc]">Slack Webhook URL</label>
                    <button
                        onClick={() => setSetupOpen(o => !o)}
                        className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300 transition-colors"
                    >
                        How to set up
                        {setupOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                    </button>
                </div>

                {setupOpen && (
                    <div className="p-3 bg-[#1a1a2e] border border-blue-900/40 rounded-lg text-xs text-[#aaaaaa] space-y-2">
                        <p className="text-[#cccccc]">Click the button below to open Slack's Incoming Webhooks page, select a channel, then copy the webhook URL and paste it here.</p>
                        <ol className="list-decimal list-inside space-y-1">
                            <li>Click <span className="text-[#cccccc]">Add to Slack</span> and pick a channel</li>
                            <li>Scroll down and copy the <span className="text-[#cccccc]">Webhook URL</span></li>
                            <li>Paste it in the field below and click <span className="text-[#cccccc]">Save Settings</span></li>
                        </ol>
                        <a
                            href="https://slack.com/apps/A0F7XDUAZ-incoming-webhooks"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors mt-1"
                        >
                            Open Slack Incoming Webhooks
                        </a>
                    </div>
                )}

                <div className="relative w-full">
                    <input
                        type={showWebhook ? 'text' : 'password'}
                        value={settings.webhook_url}
                        onChange={(e) => setSettings(prev => ({ ...prev, webhook_url: e.target.value }))}
                        placeholder={hasExistingWebhook ? '••••••••••••XXXX' : ''}
                        className="w-full bg-[#1e1e1e] border border-[#3c3c3c] rounded-lg px-3 py-2 pr-9 text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-600 focus:border-transparent outline-none transition-all font-mono text-sm"
                    />
                    <button
                        onClick={() => setShowWebhook(!showWebhook)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-300"
                    >
                        {showWebhook ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                </div>
                <p className="text-xs text-[#606060]">
                    {hasExistingWebhook
                        ? 'A webhook is configured. Enter a new URL to update it, or leave blank to keep the existing one.'
                        : 'Paste your webhook URL above, then click Save Settings.'}
                </p>
            </div>

            {/* Notification Preferences */}
            <div className="space-y-3 pt-3 border-t border-[#3c3c3c]">
                <h3 className="text-xs font-semibold text-[#808080] uppercase tracking-wide">Notification Preferences</h3>

                {[
                    { key: 'notify_on_drift_detected', label: 'Notify when drift is detected' },
                    { key: 'notify_on_drift_resolved', label: 'Notify when drift is resolved' },
                    { key: 'include_diff_summary', label: 'Include change summary in notifications' },
                    { key: 'include_link', label: 'Include link to Drift Inbox', icon: <Link2 className="w-3.5 h-3.5 inline mr-1 opacity-60" /> },
                ].map(({ key, label, icon }) => (
                    <div key={key} className="flex items-center justify-between">
                        <span className="text-sm text-[#cccccc]">{icon}{label}</span>
                        <button
                            onClick={() => handleToggle(key)}
                            className={`relative w-11 h-5 rounded-full transition-colors shrink-0 ${settings[key] ? 'bg-blue-600' : 'bg-[#4c4c4c]'}`}
                        >
                            <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${settings[key] ? 'left-6' : 'left-0.5'}`} />
                        </button>
                    </div>
                ))}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-3 border-t border-[#3c3c3c]">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded-lg transition-colors"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Save Settings
                </button>
                <button
                    onClick={handleTest}
                    disabled={testing || !hasExistingWebhook}
                    className="flex items-center gap-1.5 px-3 py-1.5 border border-[#4c4c4c] text-[#cccccc] text-sm rounded-lg hover:bg-[#3c3c3c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TestTube2 className="w-3.5 h-3.5" />}
                    Test Connection
                </button>
            </div>
        </div>
    );
};

export default SlackSettings;
